// The session hook every later frontend task reaches for to learn who is signed in — `GET /me`
// (DESIGN.md §8, api-contract.yaml `me_get`) behind TanStack Query (DESIGN.md §3). It never reads
// or decodes the session cookie itself: the cookie is `HttpOnly` (DESIGN.md §8.1.1) and the server
// is the sole authorization boundary (DESIGN.md §8.5) — this hook only reports what the server
// already decided.
'use client';

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getProfile, type Profile } from '../api';
import { getApiErrorCode } from '../errors';

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

export class SessionError extends Error {
  readonly code: string | undefined;
  constructor(code: string | undefined, message: string) {
    super(message);
    this.name = 'SessionError';
    this.code = code;
  }
}

async function fetchSession(): Promise<Profile> {
  const { data, error } = await getProfile();
  if (error) {
    throw new SessionError(getApiErrorCode(error), 'Tidak dapat memuat sesi.');
  }
  return data;
}

/** `enabled: false` lets a public page (e.g. `/masuk`) opt out entirely, so "no protected data
 * renders before [sign-in] succeeds" holds structurally, not just by convention. */
export function useSession(options: { enabled?: boolean } = {}): UseQueryResult<Profile, SessionError> {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    enabled: options.enabled ?? true,
    retry: false,
    staleTime: 30_000,
  });
}

/** Called after a successful sign-in/registration/activation so every mounted `useSession()`
 * consumer (the shell's greeting, the account page) refetches rather than showing stale data. */
export function useInvalidateSession() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}
